import React, {FC, useState, useEffect} from "react"

import {FormattedMessage} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import {H3, H5, Body, Label} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import adminTracking from "@irati-tracking/admin"

import {EnableSSOImage} from "../../../../../images/index"
import PeoplePageLink from "../../SetupInformation/PeoplePageLink"
import EnableOauth from "./EnableOAuth"
import EnableSAML from "./EnableSAML"
import Header from "./Header"

const SSO_CONFIRM_CONNECTION = "SSO_confirmConnectionType"
const SSO_GET_STARTED = "SSO_getStarted"
const SSO_OAUTH_START = "SSO_OAuthStart"
const SSO_SAML_START = "SSO_samlStart"
const UNSURE_CONNECTION = "SSO_unsureConnectionType"

interface Props {
  onClose: () => void
}

enum EnableSSOSteps {
  BusinessHasSSOProvidersStep = "BusinessHasSSOProvidersStep",
  AdminsSetStep = "AdminsSetStep",
  PeopleDataSetStep = "PeopleDataSetStep",
  CountriesAddedStep = "CountriesAddedStep"
}

const EnableSSO: FC<Props> = ({onClose}) => {
  const [notSure, setNotSure] = useState<boolean>(false)
  const [step, setStep] = useState<EnableSSOSteps>(EnableSSOSteps.BusinessHasSSOProvidersStep)

  useEffect(() => {
    adminTracking.startIntegrationsSSOJourney(SSO_GET_STARTED)
  }, [])

  const handleStartAgain = () => {
    setStep(EnableSSOSteps.BusinessHasSSOProvidersStep)
    setNotSure(false)
  }

  const handleConnection = (oauth: boolean) => {
    adminTracking.startIntegrationsSSOJourney(oauth ? SSO_OAUTH_START : SSO_SAML_START)
    adminTracking.startIntegrationsSSOJourney(SSO_CONFIRM_CONNECTION)
    return oauth ? setStep(EnableSSOSteps.AdminsSetStep) : setStep(EnableSSOSteps.PeopleDataSetStep)
  }

  const handleNotSure = () => {
    adminTracking.startIntegrationsSSOJourney(UNSURE_CONNECTION)
    setNotSure(true)
  }

  return (
    <>
      {(() => {
        switch (step) {
          case EnableSSOSteps.BusinessHasSSOProvidersStep:
            return (
              <Flex direction="column" width="100%">
                {!notSure ? (
                  <Flex direction="column">
                    <Header />
                    <Body marginBottom={2}>
                      <FormattedMessage id="admin.components.integration.sso.type" />
                    </Body>
                    <div>
                      <Button small id="settingsIntegrationSSO_OAUTH_option_button" onClick={() => handleConnection(true)} marginRight={2}>
                        <FormattedMessage id="admin.components.integration.sso.enableSSO.start.oauthBtn" />
                      </Button>
                      <Button small id="settingsIntegrationSSO_SAML_option_button" onClick={() => handleConnection(false)}>
                        <FormattedMessage id="admin.components.integration.sso.enableSSO.start.samlBtn" />
                      </Button>
                      <Button
                        small
                        tertiary
                        id="settingsIntegrationSSONotSure_button"
                        marginLeft={3}
                        onClick={handleNotSure}
                        marginTop={2}>
                        <FormattedMessage id="admin.components.integration.sso.enableSSO.start.notSureBtn" />
                      </Button>
                    </div>
                  </Flex>
                ) : (
                  <Flex direction="column">
                    <Header />
                    <H5 marginBottom={1}>
                      <FormattedMessage id="admin.components.integration.sso.enableSSO.notSure.title" />
                    </H5>
                    <Body marginBottom={4}>
                      <FormattedMessage id="admin.components.integration.sso.enableSSO.notSure.desc" />
                    </Body>
                    <PeoplePageLink />
                    <Flex justifyContent="flex-end">
                      <Button id="settingsIntegrationSSOStartAgain_button" onClick={handleStartAgain} primary>
                        <FormattedMessage id="admin.components.integration.sso.enableSSO.notSure.startAgainBtn" />
                      </Button>
                    </Flex>
                  </Flex>
                )}
              </Flex>
            )
          case EnableSSOSteps.AdminsSetStep:
            return (
              <EnableOauth
                onBack={() => setStep(EnableSSOSteps.BusinessHasSSOProvidersStep)}
                onClose={() => setStep(EnableSSOSteps.CountriesAddedStep)}
              />
            )
          case EnableSSOSteps.PeopleDataSetStep:
            return <EnableSAML onBack={() => setStep(EnableSSOSteps.BusinessHasSSOProvidersStep)} />
          case EnableSSOSteps.CountriesAddedStep:
            return (
              <Flex width="100%" justifyContent="space-between">
                <Flex direction="column">
                  <Label marginBottom={2}>
                    <FormattedMessage id="admin.components.integration.sso.title" />
                  </Label>
                  <H3 marginBottom={1}>
                    <FormattedMessage id="admin.components.integration.sso.enableSSO.oauth.done.title" />
                  </H3>
                  <Body marginBottom={4}>
                    <FormattedMessage id="admin.components.integration.sso.enableSSO.oauth.done.desc" />
                  </Body>
                  <div>
                    <Button id="settingsIntegrationSSO_OAUTHDone_button" primary onClick={onClose}>
                      <FormattedMessage id="admin.components.integration.sso.enableSSO.oauth.done.btn" />
                    </Button>
                  </div>
                </Flex>
                <EnableSSOImage />
              </Flex>
            )
          default:
            return null
        }
      })()}
    </>
  )
}

export default EnableSSO
